import { useDashboardData } from "../../../context/DashboardContext";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { branding } from "../../../config/branding";
import type { WidgetComponentProps } from "../types";

const formatCurrency = (value: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value);

export default function BarChartWidget({ config, title }: WidgetComponentProps) {
  const { chartData } = useDashboardData();

  const dataKey = config.dataKey as string;
  const valueKey = (config.valueKey as string) || "total";
  const xKey = (config.xKey as string) || "month";
  const barColor = (config.barColor as string) || branding.colors.chart[0];
  const height = (config.height as number) || 260;

  const data = (chartData[dataKey] as Record<string, unknown>[] | undefined) || [];

  if (data.length === 0) {
    return (
      <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col">
        <h3 className="text-base font-semibold mb-4">{title}</h3>
        <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
          No data available
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-xl border border-border p-6 h-full flex flex-col overflow-hidden">
      <h3 className="text-base font-semibold mb-5">{title}</h3>
      <div className="flex-1" style={{ minHeight: height }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={data}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} className="stroke-border" />
            <XAxis dataKey={xKey} tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false} />
            <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" axisLine={false} tickLine={false}
              tickFormatter={(v: number) => {
                if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`;
                if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`;
                return `$${v}`;
              }}
            />
            <Tooltip
              content={({ active, payload, label }) => {
                if (!active || !payload?.length) return null;
                return (
                  <div className="bg-card border border-border rounded-xl shadow-xl px-4 py-3 backdrop-blur-md">
                    <p className="text-sm font-medium text-muted-foreground mb-1">{String(label)}</p>
                    {payload.map((entry, i) => (
                      <p key={i} className="text-sm font-semibold" style={{ color: entry.color }}>
                        {String(entry.name)}: {formatCurrency(entry.value as number)}
                      </p>
                    ))}
                  </div>
                );
              }}
            />
            <Bar dataKey={valueKey} name={config.barName as string || "Total"} fill={barColor} radius={[6, 6, 0, 0]} barSize={24} />
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
